/* CloudDream checkout-summary augmentation for Essential Cart Drawer. */
(function (scope) {
  'use strict';

  var PORTAL_ID = 'essential-cart-drawer-portal';
  var FOOTER_SELECTOR = '[data-essential-cart-element="cart-drawer-footer"]';
  var TOTAL_LABEL_SELECTOR = '[data-essential-cart-element="cart-drawer-property-label"]';
  var DISCOUNT_AMOUNT_SELECTOR = '[data-essential-cart-element="discount-code-amount-value"]';
  var CART_ITEM_SELECTOR = '[data-essential-cart-element="cart-drawer-item"]';
  var CART_TITLE_SELECTOR = '[data-essential-cart-element="line-item-title"]';
  var CART_VARIANT_SELECTOR = '[data-essential-cart-element="line-item-variant-selector"]';
  var CART_QUANTITY_SELECTOR = '[data-essential-cart-element="quantity-value"]';
  var CART_PRICE_SELECTOR = '[data-essential-cart-element="line-item-price"]';
  var CHECKOUT_WRAPPER_SELECTOR = '[data-essential-cart-element="checkout-button-wrapper"]';
  var CHECKOUT_BUTTON_SELECTOR = '[data-essential-cart-element="cart-drawer-checkout-button"]';
  var CHECKOUT_BUTTON_TEXT_SELECTOR = '[data-essential-cart-element="cart-drawer-checkout-button-text"]';
  var CONTENT_SELECTOR = '[data-essential-cart-element="cart-drawer-content"]';
  var REWARD_MILESTONE_SELECTOR = '[data-essential-cart-element="reward-milestone"]';
  var REWARD_BAR_SELECTOR = '[data-essential-cart-element="reward-goal-bar-wrapper"]';
  var REWARD_BANNER_CLASS = 'cd-cart-reward-banner';
  var OWNED_MUTATION_SELECTOR = [
    '.cd-cart-recommendation',
    '.cd-cart-lowest',
    '.cd-cart-price-breakdown',
    '.cd-cart-reward-banner',
    '.cd-cart-grouped-price',
    '.cd-cart-grouped-quantity',
    '.cd-cart-summary__prepaid-offer',
    '.cd-cart-summary__shipping',
    '.cd-cart-summary__trust',
    '.cd-cart-upsell__copy',
  ].join(',');
  var observedPortal = null;
  var portalObserver = null;
  var priceRefreshTimer = null;
  var priceRequestId = 0;
  var productRequests = Object.create(null);
  var summarySignatures = new WeakMap();
  var lastRewardCents = null;
  var rewardBannerNode = null;
  var priceBreakdownNode = null;
  var essentialCartData = scope.CloudDreamEssentialCartData || null;

  function getStrings() {
    return scope.CloudDreamCartSummaryStrings || {};
  }

  function createSymbol(name, className, filled) {
    var symbol = document.createElement('span');
    symbol.className = 'material-symbols-outlined cd-material-symbol ' + className;
    symbol.textContent = name;
    symbol.setAttribute('aria-hidden', 'true');
    symbol.style.setProperty('--cd-symbol-fill', filled ? '1' : '0');
    return symbol;
  }

  function createTrustItem(iconName, label) {
    var item = document.createElement('div');
    item.className = 'cd-cart-summary__trust-item';
    item.setAttribute('role', 'listitem');

    item.append(createSymbol(iconName, 'cd-cart-summary__trust-icon', false));

    var text = document.createElement('span');
    text.className = 'cd-cart-summary__trust-label';
    text.textContent = label || '';
    item.append(text);
    return item;
  }

  function toCents(value) {
    var cents = Number(value);
    return Number.isFinite(cents) ? Math.max(0, Math.round(cents)) : 0;
  }

  function getPriceBreakdown(cart, variantLookup, variantDataComplete) {
    cart = cart || {};
    variantLookup = variantLookup || {};

    var items = Array.isArray(cart.items) ? cart.items : [];
    var totalCents = toCents(cart.total_price);
    var canUseVariantData =
      Boolean(variantDataComplete) &&
      items.every(function (item) {
        return Object.prototype.hasOwnProperty.call(variantLookup, String(item.variant_id));
      });
    var mrpCents;

    if (canUseVariantData) {
      mrpCents = items.reduce(function (sum, item) {
        var variant = variantLookup[String(item.variant_id)] || {};
        var originalPrice = toCents(item.original_price);
        var compareAtPrice = toCents(variant.compare_at_price);
        var quantity = Math.max(0, Number.parseInt(item.quantity, 10) || 0);
        return sum + Math.max(originalPrice, compareAtPrice) * quantity;
      }, 0);
    } else {
      mrpCents = Math.max(totalCents, toCents(cart.original_total_price));
    }

    var discountCents = Math.max(0, mrpCents - totalCents, toCents(cart.total_discount));
    mrpCents = Math.max(mrpCents, totalCents + discountCents);

    // The cart-value reward, never the per-product combo. items_subtotal_price is the
    // line total after line-item discounts, so the gap to total_price is exactly the
    // order-level money. Both fields exist on cart.js and on the app's own payload,
    // which does not reliably populate cart_level_discount_applications.
    var subtotalCents = toCents(cart.items_subtotal_price);
    var rewardCents = subtotalCents > 0 ? Math.max(0, subtotalCents - totalCents) : 0;

    if (!rewardCents) {
      var cartLevel = cart.cart_level_discount_applications;
      if (Array.isArray(cartLevel)) {
        rewardCents = cartLevel.reduce(function (sum, application) {
          return sum + toCents(application && application.total_allocated_amount);
        }, 0);
      }
    }
    rewardCents = Math.min(rewardCents, discountCents);

    return {
      discountCents: discountCents,
      hasDiscount: discountCents > 0,
      hasReward: rewardCents > 0,
      mrpCents: mrpCents,
      rewardCents: rewardCents,
      totalCents: totalCents,
    };
  }

  function getCartLineIdentity(item) {
    var properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
    var normalizedProperties = Object.keys(properties).sort().map(function (key) {
      return [key, properties[key]];
    });
    var allocation = item && item.selling_plan_allocation;
    var sellingPlan = allocation && allocation.selling_plan;
    var sellingPlanId = sellingPlan && sellingPlan.id || allocation && allocation.selling_plan_id || '';
    return [String(item && item.variant_id || ''), String(sellingPlanId), JSON.stringify(normalizedProperties)].join(':');
  }

  function getGroupedCartLines(cart, variantLookup) {
    var groups = [];
    var byIdentity = Object.create(null);
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    variantLookup = variantLookup || {};

    items.forEach(function (item, index) {
      var identity = getCartLineIdentity(item);
      var group = byIdentity[identity];
      if (!group) {
        group = {
          finalCents: 0,
          identity: identity,
          indexes: [],
          quantity: 0,
          regularCents: 0,
          variantId: String(item.variant_id || ''),
        };
        byIdentity[identity] = group;
        groups.push(group);
      }

      var quantity = Math.max(0, Number.parseInt(item.quantity, 10) || 0);
      var variant = variantLookup[String(item.variant_id)] || {};
      var regularUnit = Math.max(toCents(item.original_price), toCents(variant.compare_at_price));
      group.indexes.push(index);
      group.quantity += quantity;
      group.regularCents += regularUnit * quantity;
      group.finalCents += toCents(item.final_line_price);
    });

    return groups;
  }

  function getShopifyRoot() {
    var root = scope.Shopify && scope.Shopify.routes && scope.Shopify.routes.root;
    if (typeof root !== 'string' || !root) return '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function readJson(response) {
    if (!response.ok) throw new Error('Cart data request failed');
    return response.json();
  }

  function clearSharedCartRequest(request) {
    scope.setTimeout(function () {
      if (scope.CloudDreamCartRequest === request) delete scope.CloudDreamCartRequest;
    }, 0);
  }

  function getPortalCartLines(portal) {
    if (!portal || !portal.querySelectorAll) return [];
    return Array.from(portal.querySelectorAll(CART_ITEM_SELECTOR)).map(function (item) {
      var title = item.querySelector(CART_TITLE_SELECTOR);
      var quantity = item.querySelector(CART_QUANTITY_SELECTOR);
      var href = title && title.getAttribute('href') || '';
      var variantId = '';
      try {
        variantId = new URL(href, 'https://clouddream.invalid').searchParams.get('variant') || '';
      } catch (_error) {
        variantId = '';
      }
      return {
        quantity: Math.max(1, Number.parseInt(quantity && quantity.textContent, 10) || 1),
        variantId: String(variantId),
      };
    }).filter(function (line) { return line.variantId; }).sort(function (a, b) {
      return a.variantId.localeCompare(b.variantId);
    });
  }

  function cartMatchesPortal(cart, portal) {
    if (!cart || !Array.isArray(cart.items)) return false;
    var cartLines = cart.items.map(function (item) {
      return {
        quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1),
        variantId: String(item.variant_id || item.id || ''),
      };
    }).filter(function (line) { return line.variantId; }).sort(function (a, b) {
      return a.variantId.localeCompare(b.variantId);
    });
    var portalLines = getPortalCartLines(portal);
    return cartLines.length === portalLines.length && cartLines.every(function (line, index) {
      return line.variantId === portalLines[index].variantId && line.quantity === portalLines[index].quantity;
    });
  }

  function setEssentialCartData(cart) {
    essentialCartData = cart && Array.isArray(cart.items) ? cart : null;
    if (essentialCartData) scope.CloudDreamEssentialCartData = essentialCartData;
    else delete scope.CloudDreamEssentialCartData;
    return essentialCartData;
  }

  function requestCart() {
    return scope.fetch(getShopifyRoot() + 'cart.js', { headers: { Accept: 'application/json' } }).then(readJson);
  }

  function fetchCart(portal) {
    if (essentialCartData && cartMatchesPortal(essentialCartData, portal)) {
      return Promise.resolve(essentialCartData);
    }
    var request = scope.CloudDreamCartRequest;
    if (!request) {
      request = requestCart();
      scope.CloudDreamCartRequest = request;
      request.then(
        function () { clearSharedCartRequest(request); },
        function () { clearSharedCartRequest(request); },
      );
    }
    // A shared read started before the last cart mutation resolves with the cart
    // as it was, which is how the reward line went missing on a cart that had
    // just earned one. One fresh read settles it.
    return request.then(function (cart) {
      if (cartMatchesPortal(cart, portal)) return cart;
      return requestCart().catch(function () { return cart; });
    });
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.reject(new Error('Cart item is missing its product handle'));
    if (productRequests[handle]) return productRequests[handle];

    productRequests[handle] = scope
      .fetch(getShopifyRoot() + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' },
      })
      .then(readJson)
      .catch(function (error) {
        delete productRequests[handle];
        throw error;
      });
    return productRequests[handle];
  }

  function getVariantLookupFromCatalog(cart, products) {
    var handles = Array.from(new Set((cart.items || []).map(function (item) { return item.handle; }))).filter(Boolean);
    var lookup = {};
    var matchedHandles = new Set();

    (products || []).forEach(function (product) {
      if (!product || !handles.includes(product.productKey)) return;
      matchedHandles.add(product.productKey);
      (product.availableVariants || []).forEach(function (variant) {
        lookup[String(variant.id)] = { compare_at_price: variant.compareAtPrice };
      });
    });

    return { complete: handles.every(function (handle) { return matchedHandles.has(handle); }), lookup: lookup };
  }

  function fetchVariantLookupFromProducts(cart) {
    var handles = Array.from(
      new Set(
        (cart.items || []).map(function (item) {
          return item.handle;
        }),
      ),
    ).filter(Boolean);

    return Promise.all(
      handles.map(function (handle) {
        return fetchProduct(handle).catch(function () {
          return null;
        });
      }),
    ).then(function (products) {
      var lookup = {};
      var complete = products.length === handles.length && products.every(Boolean);

      products.filter(Boolean).forEach(function (product) {
        (product.variants || []).forEach(function (variant) {
          lookup[String(variant.id)] = variant;
        });
      });

      return { complete: complete, lookup: lookup };
    });
  }

  function fetchVariantLookup(cart) {
    var sharedCatalog = scope.CloudDreamCatalogPromise;
    if (!sharedCatalog || typeof sharedCatalog.then !== 'function') return fetchVariantLookupFromProducts(cart);
    return sharedCatalog
      .then(function (products) {
        var shared = getVariantLookupFromCatalog(cart, products);
        return shared.complete ? shared : fetchVariantLookupFromProducts(cart);
      })
      .catch(function () { return fetchVariantLookupFromProducts(cart); });
  }

  function formatCartMoney(cents, format) {
    var money = scope.CloudDreamMoney;
    if (!money || typeof money.money !== 'function' || typeof format !== 'string' || format.indexOf('{{') === -1) {
      return '';
    }
    return money.money(cents, format);
  }

  function getDomVariantId(item) {
    var title = item && item.querySelector ? item.querySelector(CART_TITLE_SELECTOR) : null;
    var href = title && title.getAttribute('href') || '';
    try {
      return String(new URL(href, 'https://clouddream.invalid').searchParams.get('variant') || '');
    } catch (_error) {
      return '';
    }
  }

  function resetGroupedCartLines(portal) {
    Array.from(portal.querySelectorAll('.cd-cart-line-wrapper--duplicate')).forEach(function (wrapper) {
      wrapper.classList.remove('cd-cart-line-wrapper--duplicate');
      wrapper.hidden = false;
    });
    Array.from(portal.querySelectorAll('[data-cd-grouped-quantity]')).forEach(function (item) {
      item.removeAttribute('data-cd-grouped-quantity');
    });
    Array.from(portal.querySelectorAll('.cd-cart-grouped-price, .cd-cart-grouped-quantity')).forEach(function (node) {
      node.remove();
    });
  }

  function createGroupedPrice(group, strings) {
    var regular = formatCartMoney(group.regularCents, strings.moneyFormat);
    var final = formatCartMoney(group.finalCents, strings.moneyFormat);
    if (!final) return null;

    var container = document.createElement('div');
    container.className = 'cd-cart-grouped-price';

    if (regular && group.regularCents > group.finalCents) {
      var compare = document.createElement('span');
      compare.className = 'cd-cart-grouped-price__compare';
      compare.textContent = regular;
      container.append(compare);
    }

    var current = document.createElement('span');
    current.className = 'cd-cart-grouped-price__current';
    current.textContent = final;
    container.append(current);
    container.setAttribute('aria-label', [regular, final].filter(Boolean).join(', '));
    return container;
  }

  function groupDuplicateCartLines(portal, cart, variantLookup, strings) {
    if (!portal || !portal.querySelectorAll) return false;
    resetGroupedCartLines(portal);

    var domItems = Array.from(portal.querySelectorAll(CART_ITEM_SELECTOR));
    var cartItems = cart && Array.isArray(cart.items) ? cart.items : [];
    if (domItems.length !== cartItems.length) return false;
    if (domItems.some(function (item, index) {
      return getDomVariantId(item) !== String(cartItems[index].variant_id || '');
    })) return false;

    var changed = false;
    getGroupedCartLines(cart, variantLookup).forEach(function (group) {
      if (group.indexes.length < 2) return;
      var primary = domItems[group.indexes[0]];
      var quantityValue = primary.querySelector(CART_QUANTITY_SELECTOR);
      var quantityControl = quantityValue && quantityValue.parentElement;
      var price = primary.querySelector(CART_PRICE_SELECTOR);
      if (!quantityValue || !quantityControl || !price) return;

      primary.setAttribute('data-cd-grouped-quantity', String(group.quantity));
      var groupedQuantity = document.createElement('span');
      groupedQuantity.className = 'cd-cart-grouped-quantity';
      groupedQuantity.textContent = String(group.quantity);
      groupedQuantity.setAttribute('aria-hidden', 'true');
      quantityControl.append(groupedQuantity);

      var groupedPrice = createGroupedPrice(group, strings);
      if (groupedPrice) price.append(groupedPrice);

      group.indexes.slice(1).forEach(function (index) {
        var duplicate = domItems[index];
        var wrapper = duplicate && duplicate.parentElement;
        if (!wrapper) return;
        wrapper.classList.add('cd-cart-line-wrapper--duplicate');
        wrapper.hidden = true;
      });
      changed = true;
    });
    return changed;
  }

  function isFreeShippingUnlocked(portal) {
    var shippingMilestone = portal.querySelector(REWARD_MILESTONE_SELECTOR);
    return Boolean(shippingMilestone && /: unlocked\s*$/i.test(shippingMilestone.getAttribute('aria-label') || ''));
  }

  function setPriceBreakdownExpanded(section, expanded) {
    if (!section || !section.querySelector) return false;
    var toggle = section.querySelector('.cd-cart-price-breakdown__toggle');
    var panel = section.querySelector('.cd-cart-price-breakdown__card');
    var icon = section.querySelector('.cd-cart-price-breakdown__toggle-icon');
    var isExpanded = Boolean(expanded);

    if (toggle) toggle.setAttribute('aria-expanded', String(isExpanded));
    if (panel) panel.hidden = !isExpanded;
    if (icon) icon.textContent = isExpanded ? 'keyboard_arrow_down' : 'add';
    if (section.classList) section.classList.toggle('is-expanded', isExpanded);
    return isExpanded;
  }

  function createPriceRow(label, value, modifier) {
    var row = document.createElement('div');
    row.className = 'cd-cart-price-breakdown__row' + (modifier ? ' cd-cart-price-breakdown__row--' + modifier : '');

    var rowLabel = document.createElement('span');
    rowLabel.className = 'cd-cart-price-breakdown__label';
    rowLabel.textContent = label || '';

    var rowValue = document.createElement('span');
    rowValue.className = 'cd-cart-price-breakdown__value';
    rowValue.textContent = value || '';

    row.append(rowLabel, rowValue);
    return row;
  }

  function updatePriceBreakdown(section, strings, breakdown) {
    var currentToggle = section.querySelector('.cd-cart-price-breakdown__toggle');
    var wasExpanded = Boolean(currentToggle && currentToggle.getAttribute('aria-expanded') === 'true');
    section.className = 'cd-cart-price-breakdown';
    section.setAttribute('aria-labelledby', 'cd-cart-price-breakdown-title');

    var title = document.createElement('h2');
    title.className = 'cd-cart-price-breakdown__title';
    title.id = 'cd-cart-price-breakdown-title';

    var toggle = document.createElement('button');
    toggle.className = 'cd-cart-price-breakdown__toggle';
    toggle.type = 'button';
    toggle.setAttribute('aria-controls', 'cd-cart-price-breakdown-panel');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.append(document.createTextNode(strings.priceBreakdown || ''));
    toggle.append(createSymbol('add', 'cd-cart-price-breakdown__toggle-icon', false));
    title.append(toggle);

    var card = document.createElement('div');
    card.className =
      'cd-cart-price-breakdown__card' +
      (breakdown.hasDiscount ? '' : ' cd-cart-price-breakdown__card--compact');
    card.id = 'cd-cart-price-breakdown-panel';

    if (breakdown.hasDiscount) {
      card.append(createPriceRow(strings.mrp, breakdown.mrp));
    }

    card.append(
      createPriceRow(
        strings.deliveryFee,
        breakdown.shippingUnlocked ? strings.free : strings.calculatedAtCheckout,
        breakdown.shippingUnlocked ? 'free' : 'shipping',
      ),
    );

    if (breakdown.hasDiscount) {
      var discountRow = createPriceRow(strings.discount, breakdown.discount, 'discount');
      var status = document.createElement('span');
      status.className = 'cd-cart-price-breakdown__status';
      status.append(createSymbol('percent_discount', 'cd-cart-price-breakdown__status-icon', true));
      status.append(document.createTextNode(strings.appliedStatus || ''));
      discountRow.querySelector('.cd-cart-price-breakdown__label').append(status);
      card.append(discountRow);
    }

    card.append(createPriceRow(strings.total, breakdown.total, 'total'));

    section.replaceChildren(title, card);
    toggle.addEventListener('click', function () {
      setPriceBreakdownExpanded(section, toggle.getAttribute('aria-expanded') !== 'true');
    });
    setPriceBreakdownExpanded(section, wasExpanded);
    return section;
  }

  function createPriceBreakdown(strings, breakdown) {
    return updatePriceBreakdown(document.createElement('section'), strings, breakdown);
  }

  function createRewardBanner(strings) {
    var banner = document.createElement('aside');
    banner.className = REWARD_BANNER_CLASS;

    var mark = document.createElement('span');
    mark.className = 'cd-cart-reward-banner__mark';
    mark.append(createSymbol('check', 'cd-cart-reward-banner__icon', true));

    var body = document.createElement('div');
    body.className = 'cd-cart-reward-banner__body';

    var label = document.createElement('span');
    label.className = 'cd-cart-reward-banner__label';
    label.textContent = strings.rewardUnlocked || '';

    var amount = document.createElement('span');
    amount.className = 'cd-cart-reward-banner__amount';

    var note = document.createElement('span');
    note.className = 'cd-cart-reward-banner__note';
    note.textContent = strings.rewardUnlockedNote || '';

    body.append(label, amount, note);
    banner.append(mark, body);
    return banner;
  }

  function removeRewardBanner(portal) {
    var existing = portal && portal.querySelector ? portal.querySelector('.' + REWARD_BANNER_CLASS) : null;
    if (existing) existing.remove();
    rewardBannerNode = null;
    lastRewardCents = null;
  }

  function ensureRewardBanner(portal, strings, breakdown) {
    var existing = portal.querySelector('.' + REWARD_BANNER_CLASS);
    var anchor = portal.querySelector(REWARD_BAR_SELECTOR);
    var content = portal.querySelector(CONTENT_SELECTOR);

    if (!breakdown || !breakdown.hasReward || !breakdown.reward) {
      if (existing) existing.remove();
      rewardBannerNode = null;
      // A cart that qualifies later should still animate, so only a real zero resets.
      lastRewardCents = breakdown && !breakdown.hasReward ? 0 : lastRewardCents;
      return null;
    }

    // The app rebuilds the reward bar on every cart change. Missing it for one
    // pass is not the same as having no reward, so an already-placed banner is
    // left alone and the layout pass re-seats it when the bar comes back.
    if (!anchor && !content) return existing || null;
    if (!anchor && existing) return existing;

    var banner = existing || createRewardBanner(strings);
    var amount = banner.querySelector('.cd-cart-reward-banner__amount');
    if (amount) amount.textContent = '\u2212 ' + breakdown.reward;

    // Plays once, on the locked -> unlocked crossing. An already-qualifying cart opens at rest.
    if (lastRewardCents === 0 && breakdown.rewardCents > 0) {
      banner.classList.remove('is-landing');
      void banner.offsetWidth;
      banner.classList.add('is-landing');
      banner.addEventListener(
        'animationend',
        function () {
          banner.classList.remove('is-landing');
        },
        { once: true },
      );
    }
    lastRewardCents = breakdown.rewardCents;

    if (anchor) {
      if (anchor.nextElementSibling !== banner) anchor.after(banner);
    } else {
      // Brand new, with no bar to sit under yet: the top of the column is the
      // closest slot, and the layout pass corrects it on the next mutation.
      content.prepend(banner);
    }
    rewardBannerNode = banner;
    return banner;
  }

  function getPriceBreakdowns(portal) {
    var sections = Array.from(portal.querySelectorAll('.cd-cart-price-breakdown'));
    sections.slice(1).forEach(function (section) {
      section.remove();
    });
    return sections.slice(0, 1);
  }

  function removePriceBreakdowns(portal) {
    getPriceBreakdowns(portal).forEach(function (section) {
      section.remove();
    });
    priceBreakdownNode = null;
  }

  function applyCartLayout(portal) {
    var layout = scope.CloudDreamCartLayout;
    return Boolean(layout && layout.apply && layout.apply(portal));
  }

  // The breakdown used to anchor to .cd-cart-lowest, so a cart with no
  // recommendations put it in a different slot than one with them. Placement is
  // now the layout module's job, against the app's own nodes only.
  function placeExistingPriceBreakdown(portal) {
    var existing = getPriceBreakdowns(portal)[0];
    if (!existing) return null;
    applyCartLayout(portal);
    return existing;
  }

  function ensurePriceBreakdown(portal, strings, breakdown) {
    var content = portal.querySelector(CONTENT_SELECTOR);
    var existing = getPriceBreakdowns(portal)[0];

    if (!content || !breakdown) {
      removePriceBreakdowns(portal);
      return null;
    }

    var signature = JSON.stringify(breakdown);
    if (!existing) {
      existing = createPriceBreakdown(strings, breakdown);
      content.append(existing);
    } else if (existing.getAttribute('data-cd-price-signature') !== signature) {
      updatePriceBreakdown(existing, strings, breakdown);
    }
    existing.setAttribute('data-cd-price-signature', signature);
    priceBreakdownNode = existing;
    return placeExistingPriceBreakdown(portal);
  }

  function refreshPriceBreakdown(portal, strings, requestId) {
    return fetchCart(portal)
      .then(function (cart) {
        return fetchVariantLookup(cart).then(function (variants) {
          return { cart: cart, variants: variants };
        });
      })
      .then(function (result) {
        if (requestId !== priceRequestId || portal !== observedPortal) return false;
        if (!result.cart.item_count) {
          resetGroupedCartLines(portal);
          removePriceBreakdowns(portal);
          removeRewardBanner(portal);
          return true;
        }

        groupDuplicateCartLines(portal, result.cart, result.variants.lookup, strings);
        var values = getPriceBreakdown(result.cart, result.variants.lookup, result.variants.complete);
        var breakdown = {
          discount: formatCartMoney(values.discountCents, strings.moneyFormat),
          hasDiscount: values.hasDiscount,
          mrp: formatCartMoney(values.mrpCents, strings.moneyFormat),
          hasReward: values.hasReward,
          reward: formatCartMoney(values.rewardCents, strings.moneyFormat),
          rewardCents: values.rewardCents,
          shippingUnlocked: isFreeShippingUnlocked(portal),
          total: formatCartMoney(values.totalCents, strings.moneyFormat),
        };

        if (!breakdown.total || (values.hasDiscount && (!breakdown.mrp || !breakdown.discount))) {
          throw new Error('Cart money formatter is unavailable');
        }

        if (breakdown.hasReward && !breakdown.reward) breakdown.hasReward = false;

        ensurePriceBreakdown(portal, strings, breakdown);
        ensureRewardBanner(portal, strings, breakdown);
        return true;
      })
      .catch(function () {
        if (requestId === priceRequestId && portal === observedPortal) placeExistingPriceBreakdown(portal);
        return false;
      });
  }

  function schedulePriceBreakdown(portal, strings) {
    if (!portal || !portal.querySelector || !portal.querySelector(CART_ITEM_SELECTOR)) return;
    applyCartLayout(portal);
    priceRequestId += 1;
    var requestId = priceRequestId;
    if (priceRefreshTimer) clearTimeout(priceRefreshTimer);

    priceRefreshTimer = setTimeout(function () {
      priceRefreshTimer = null;
      refreshPriceBreakdown(portal, strings, requestId);
    }, 60);
  }

  // Sits beside the total, and only once the app says the milestone is actually
  // unlocked — a shopper who has not earned free shipping must not be told they have.
  function ensureFreeShippingPill(footer, strings, unlocked) {
    var pill = footer.querySelector('.cd-cart-summary__shipping');
    var label = strings.freeShippingEarned || '';

    if (!label || !unlocked) {
      if (pill) pill.remove();
      return null;
    }

    if (!pill) {
      pill = document.createElement('span');
      pill.className = 'cd-cart-summary__shipping';
      pill.append(createSymbol('local_shipping', 'cd-cart-summary__shipping-icon', true));
      var text = document.createElement('span');
      text.className = 'cd-cart-summary__shipping-label';
      pill.append(text);
      footer.append(pill);
    }

    var text = pill.querySelector('.cd-cart-summary__shipping-label');
    if (text && text.textContent !== label) text.textContent = label;
    return pill;
  }

  function ensurePrepaidOffer(checkoutWrapper, strings) {
    var offer = checkoutWrapper.querySelector('.cd-cart-summary__prepaid-offer');
    var label = strings.prepaidOffer || '';

    if (!label) {
      if (offer) offer.remove();
      return null;
    }

    if (!offer) {
      offer = document.createElement('span');
      offer.className = 'cd-cart-summary__prepaid-offer';
      offer.id = 'CloudDreamPrepaidOffer';
      checkoutWrapper.prepend(offer);
    }

    if (offer.textContent !== label) offer.textContent = label;

    // The pill is pointer-transparent decoration, so the offer reaches a screen
    // reader through the button it sits on rather than as a stray label.
    var checkoutButton = checkoutWrapper.querySelector(CHECKOUT_BUTTON_SELECTOR);
    if (checkoutButton) {
      var describedBy = (checkoutButton.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (describedBy.indexOf(offer.id) === -1) {
        describedBy.push(offer.id);
        checkoutButton.setAttribute('aria-describedby', describedBy.join(' '));
      }
    }

    return offer;
  }

  function ensureTrustRow(footer, checkoutWrapper, strings) {
    var trustRow = footer.querySelector('.cd-cart-summary__trust');
    if (trustRow) return trustRow;

    trustRow = document.createElement('div');
    trustRow.className = 'cd-cart-summary__trust';
    trustRow.setAttribute('role', 'list');
    trustRow.setAttribute('aria-label', strings.reassurance || '');
    trustRow.append(
      createTrustItem('local_shipping', strings.shipping),
      createTrustItem('assignment_return', strings.returns),
    );
    checkoutWrapper.insertAdjacentElement('afterend', trustRow);
    return trustRow;
  }

  function decorateCartSummary(portal) {
    if (!portal) return false;

    placeExistingPriceBreakdown(portal);

    var footer = portal.querySelector(FOOTER_SELECTOR);
    var checkoutWrapper = footer && footer.querySelector(CHECKOUT_WRAPPER_SELECTOR);
    if (!footer || !checkoutWrapper) return false;

    var strings = getStrings();
    var totalLabel = footer.querySelector(TOTAL_LABEL_SELECTOR);
    if (totalLabel && strings.total && totalLabel.textContent !== strings.total) {
      totalLabel.textContent = strings.total;
    }

    var checkoutButtonText = footer.querySelector(CHECKOUT_BUTTON_TEXT_SELECTOR);
    var checkoutLabel = scope.cartStrings && scope.cartStrings.checkout;
    if (checkoutButtonText && checkoutLabel && checkoutButtonText.textContent !== checkoutLabel) {
      checkoutButtonText.textContent = checkoutLabel;
    }

    ensureFreeShippingPill(footer, strings, isFreeShippingUnlocked(portal));
    ensurePrepaidOffer(checkoutWrapper, strings);
    ensureTrustRow(footer, checkoutWrapper, strings);
    footer.setAttribute('data-cd-checkout-summary', '');
    return true;
  }

  function isInsideOwnedMutation(node) {
    var element = node && (node.nodeType === 1 ? node : node.parentElement);
    return Boolean(element && element.closest && element.closest(OWNED_MUTATION_SELECTOR));
  }

  function mutationNeedsPriceRefresh(mutation) {
    if (isInsideOwnedMutation(mutation.target)) return false;
    if (mutation.type !== 'childList') return true;

    var changedNodes = Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes));
    if (!changedNodes.length) return true;

    return changedNodes.some(function (node) {
      return !isInsideOwnedMutation(node);
    });
  }

  function getCartDomSignature(portal) {
    if (!portal || !portal.querySelectorAll) return '';
    return Array.from(portal.querySelectorAll(CART_ITEM_SELECTOR)).map(function (item) {
      var title = item.querySelector(CART_TITLE_SELECTOR);
      var variant = item.querySelector(CART_VARIANT_SELECTOR);
      var quantity = item.querySelector(CART_QUANTITY_SELECTOR);
      return [
        title && (title.getAttribute('href') || title.textContent.trim()),
        variant && (variant.value || variant.textContent.trim()),
        quantity && quantity.textContent.trim(),
      ].join(':');
    }).join('|');
  }

  function hasMissingOwnSection() {
    return Boolean(
      (rewardBannerNode && !rewardBannerNode.isConnected) ||
      (priceBreakdownNode && !priceBreakdownNode.isConnected),
    );
  }

  function getSummaryStateSignature(portal) {
    if (!portal || !portal.querySelector) return '';
    var subtotal = portal.querySelector('[data-essential-cart-element="subtotal-wrapper"]');
    var discount = portal.querySelector(DISCOUNT_AMOUNT_SELECTOR);
    var shipping = portal.querySelector(REWARD_MILESTONE_SELECTOR);
    return [
      getCartDomSignature(portal),
      subtotal && subtotal.textContent.trim(),
      discount && discount.textContent.trim(),
      shipping && shipping.getAttribute('aria-label'),
    ].join('|');
  }

  function observePortal(portal) {
    if (!portal || portal === observedPortal) return;

    if (portalObserver) portalObserver.disconnect();
    observedPortal = portal;
    decorateCartSummary(portal);
    summarySignatures.set(portal, getSummaryStateSignature(portal));

    portalObserver = new MutationObserver(function (mutations) {
      applyCartLayout(portal);
      decorateCartSummary(portal);
      var missing = hasMissingOwnSection();
      if (!missing && !mutations.some(mutationNeedsPriceRefresh)) return;
      var signature = getSummaryStateSignature(portal);
      // An unchanged signature normally means there is nothing to redraw, but it
      // says nothing about whether our sections survived the app's re-render.
      if (!missing && signature === summarySignatures.get(portal)) return;
      summarySignatures.set(portal, signature);
      schedulePriceBreakdown(portal, getStrings());
    });
    portalObserver.observe(portal, {
      attributeFilter: ['aria-label'],
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });
    schedulePriceBreakdown(portal, getStrings());
  }

  function handleEssentialCartUpdate(event) {
    var cart = setEssentialCartData(event && event.detail);
    if (!cart || !observedPortal) return;
    if (!cart.item_count) {
      removePriceBreakdowns(observedPortal);
      return;
    }
    if (observedPortal.querySelector(CART_ITEM_SELECTOR)) {
      schedulePriceBreakdown(observedPortal, getStrings());
    }
  }

  function findPortal() {
    observePortal(document.getElementById(PORTAL_ID));
  }

  function init() {
    document.addEventListener('essential-cart:update', handleEssentialCartUpdate);
    findPortal();
    if (observedPortal) return;

    var mountObserver = new MutationObserver(function () {
      findPortal();
      if (observedPortal) mountObserver.disconnect();
    });
    mountObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      decorateCartSummary: decorateCartSummary,
      cartMatchesPortal: cartMatchesPortal,
      fetchCart: fetchCart,
      getGroupedCartLines: getGroupedCartLines,
      getPriceBreakdown: getPriceBreakdown,
      getSummaryStateSignature: getSummaryStateSignature,
      getVariantLookupFromCatalog: getVariantLookupFromCatalog,
      mutationNeedsPriceRefresh: mutationNeedsPriceRefresh,
      setPriceBreakdownExpanded: setPriceBreakdownExpanded,
      setEssentialCartData: setEssentialCartData,
    };
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
    else init();
  }
})(typeof window !== 'undefined' ? window : globalThis);
